import {
  isTelegramWebApp,
  resolveTelegramInitData,
  setCachedTelegramInitData,
} from '../utils/telegramWebApp';

const API_PREFIX = import.meta.env.VITE_API_PREFIX || '/user/api';
const CSRF_HEADER = 'X-Portal-CSRF-Token';
const SESSION_STORAGE_KEY = 'yp_portal_session_id';
const DEFAULT_SESSION_HEADER = 'X-Portal-Session-Id';

/** Join portal API prefix with a path like `/vpn/keys/1/config/file?token=…`. */
export function portalApiUrl(path: string): string {
  const base = API_PREFIX.endsWith('/') ? API_PREFIX.slice(0, -1) : API_PREFIX;
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

export interface ApiError {
  code: string;
  message: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
}

export interface PortalPublicConfig {
  user_portal_url: string;
  portal_api_prefix: string;
  bot_username: string;
  csrf_header_name: string;
  session_header_name?: string;
  admin_panel_url?: string;
}

export interface PortalUserProfile {
  telegram_user_id: number;
  username?: string;
  first_name?: string;
  photo_url?: string;
}

export type PortalSupportMessageType =
  | 'text'
  | 'image'
  | 'video'
  | 'voice'
  | 'album'
  | 'sticker'
  | 'gif'
  | 'video_note';

export interface PortalSupportAttachmentDto {
  index: number;
  name?: string | null;
  mime?: string | null;
  size?: number | null;
}

export interface PortalSupportMessageDto {
  id: number;
  ticket_id: number;
  author_telegram_id: number;
  author_role: 'user' | 'support';
  message_type?: PortalSupportMessageType;
  body: string;
  created_at: string;
  edited_at?: string | null;
  forwarded_from_message_id?: number | null;
  attachment_name?: string;
  attachment_mime?: string;
  attachment_size?: number;
  duration_ms?: number;
  has_attachment?: boolean;
  attachments?: PortalSupportAttachmentDto[];
  reactions?: Array<{
    emoji: string;
    count: number;
    me?: boolean;
    user_ids?: number[];
  }>;
  forwarded_bundle?: Array<{
    from_message_id: number;
    author_role: 'user' | 'support';
    message_type?: PortalSupportMessageType;
    body: string;
    attachment_name?: string;
    attachment_mime?: string;
    attachment_size?: number;
    duration_ms?: number;
    media_index: number;
    media_count: number;
    attachments?: PortalSupportAttachmentDto[];
  }>;
}

export interface PortalSupportTicketDetailDto {
  id: number;
  user_id: number;
  subject: string;
  status: 'open' | 'answered' | 'closed';
  created_at: string;
  updated_at: string;
  last_message_preview?: string;
  last_message_id?: number | null;
  last_message_type?: PortalSupportMessageType | null;
  last_message_media_count?: number | null;
  last_message_author_role?: 'user' | 'support' | null;
  messages: PortalSupportMessageDto[];
}

export interface PortalSupportTicketListItemDto {
  id: number;
  user_id: number;
  subject: string;
  status: 'open' | 'answered' | 'closed';
  created_at: string;
  updated_at: string;
  last_message_preview?: string;
  last_message_id?: number | null;
  last_message_type?: PortalSupportMessageType | null;
  last_message_media_count?: number | null;
  last_message_author_role?: 'user' | 'support' | null;
}

export interface PortalWebsiteAccountInfo {
  username: string;
  is_new?: boolean;
  password?: string | null;
}

export interface PortalAuthMeResponse {
  authenticated: boolean;
  user?: PortalUserProfile;
  csrf_token?: string;
  session_id?: string;
  is_admin?: boolean;
  website_account?: PortalWebsiteAccountInfo | null;
}

export interface PortalSubscriptionInfo {
  subscription_tier?: string;
  subscription_until?: string;
  subscription_status: string;
  active_keys_count: number;
  wg_keys_count: number;
  ovpn_keys_count: number;
  max_keys_per_region: number;
  accessible_regions_count: number;
  max_wg_keys: number;
  max_ovpn_keys: number;
  max_vpn_keys: number;
}

export interface PortalProfile extends PortalUserProfile {
  language: string;
  is_premium: boolean;
  subscription: PortalSubscriptionInfo;
}

export interface PortalVpnKeyItem {
  id: number;
  server_country: string;
  client_id: string;
  created_at: string;
  protocol?: string;
  ip_address?: string | null;
}

export interface PortalRegionItem {
  code: string;
  name: string;
  city?: string | null;
  capacity_mbps: number;
  probe_url?: string | null;
  protocols: string[];
}

export interface PortalOvpnKeyItem {
  id: number;
  server_code: string;
  profile_id: string;
  created_at: string;
  expires_at?: string;
  ip_address?: string | null;
}

export interface PortalVpnKeyConfig {
  key_id: number;
  server_code: string;
  config_text: string;
  protocol?: string;
}

export interface PortalConfigDownloadLink {
  url: string;
  filename: string;
  expires_in: number;
}

export interface PortalInviteInfo {
  code: string;
  bonus_days: number;
  subscription_tier: string;
  share_url?: string;
  banner_url?: string;
  friends_count?: number;
  friends_active_count?: number;
  bonus_days_earned?: number;
  days_until_change_allowed?: number | null;
  banner_prefs?: Record<string, unknown> | null;
}

export interface PortalInviteeItem {
  display_name: string;
  used_at?: string | null;
}

export interface PortalActivationItem {
  code: string;
  kind: 'promo' | 'invite' | string;
  bonus_days?: number | null;
  used_at?: string | null;
}

export interface PortalPromoHistory {
  invitees: PortalInviteeItem[];
  activations: PortalActivationItem[];
}

export interface PublicInviteInfo {
  code: string;
  bonus_days: number;
  subscription_tier: string;
  invite_url: string;
}

export interface UpdateReferralCodeResponse {
  code: string;
  status: string;
  days_until_change_allowed?: number | null;
}

export interface PortalPaymentItem {
  id: string;
  period: string;
  subscription_tier?: string;
  days: number;
  amount: string;
  status: string;
  created_at: string;
}

export interface RedeemPromoResponse {
  code: string;
  reward_type: 'promo' | 'invite';
  bonus_days: number;
  added_days: number;
  subscription_tier: string;
  subscription_until: string;
}

export interface PortalTariffPeriod {
  period: string;
  label: string;
  days: number;
  price: number;
  currency: string;
  intro?: boolean;
  regular_price?: number | null;
}

export interface PortalTariffTier {
  tier_id: string;
  name: string;
  periods: PortalTariffPeriod[];
}

export interface PortalSubscriptionOffers {
  currency: string;
  tiers: PortalTariffTier[];
}

export interface PortalTierChangePreview {
  remaining_days: number;
  current_tier: string;
  new_tier: string;
  purchased_days: number;
  converted_days: number;
  total_days: number;
  is_downgrade: boolean;
}

export interface CreatePortalPaymentResponse {
  payment_id: string;
  confirmation_url: string;
  amount: number;
  currency: string;
  days: number;
  tier_id: string;
  period: string;
}

export interface PortalPaymentStatusResponse {
  payment_id: string;
  status: string;
  subscription_until?: string;
  subscription_tier?: string;
}

const PENDING_PAYMENT_KEY = 'yp_pending_payment';

export function setPendingPaymentId(paymentId: string): void {
  sessionStorage.setItem(PENDING_PAYMENT_KEY, paymentId);
}

export function getPendingPaymentId(): string | null {
  return sessionStorage.getItem(PENDING_PAYMENT_KEY);
}

export function clearPendingPaymentId(): void {
  sessionStorage.removeItem(PENDING_PAYMENT_KEY);
}

let csrfToken: string | null = null;
let portalSessionId: string | null = sessionStorage.getItem(SESSION_STORAGE_KEY);
let sessionHeaderName = DEFAULT_SESSION_HEADER;
let reloginPromise: Promise<boolean> | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function setPortalSessionHeaderName(name: string | undefined): void {
  sessionHeaderName = name?.trim() || DEFAULT_SESSION_HEADER;
}

export function setPortalSessionId(id: string | null): void {
  portalSessionId = id;
  if (id) sessionStorage.setItem(SESSION_STORAGE_KEY, id);
  else sessionStorage.removeItem(SESSION_STORAGE_KEY);
}

export function getPortalSessionId(): string | null {
  return portalSessionId;
}

export function applyPortalAuth(data: PortalAuthMeResponse | undefined): void {
  if (data?.authenticated) {
    setCsrfToken(data.csrf_token ?? null);
    if (data.session_id) setPortalSessionId(data.session_id);
  } else {
    setCsrfToken(null);
    setPortalSessionId(null);
  }
}

async function reloginViaTelegram(): Promise<boolean> {
  if (reloginPromise) return reloginPromise;
  reloginPromise = (async () => {
    try {
      if (!isTelegramWebApp()) return false;
      const initData = await resolveTelegramInitData(5000);
      if (!initData) return false;
      setCachedTelegramInitData(initData);
      const res = await rawRequest<PortalAuthMeResponse>('/auth/telegram-webapp', {
        method: 'POST',
        body: JSON.stringify({ init_data: initData }),
      });
      if (res.success && res.data?.authenticated) {
        applyPortalAuth(res.data);
        return true;
      }
      return false;
    } finally {
      reloginPromise = null;
    }
  })();
  return reloginPromise;
}

/** Re-establish portal session (Telegram WebView often drops HttpOnly cookies). */
export async function ensurePortalSession(): Promise<boolean> {
  if (portalSessionId) {
    const me = await rawRequest<PortalAuthMeResponse>('/auth/me');
    if (me.success && me.data?.authenticated) {
      applyPortalAuth(me.data);
      return true;
    }
  }
  return reloginViaTelegram();
}

async function rawRequest<T>(path: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const method = (options.method ?? 'GET').toUpperCase();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (options.body && !isFormData && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  // Login endpoints must not send a stale session/CSRF from a previous visit.
  const isAuthBootstrap =
    path === '/auth/password' ||
    path === '/auth/telegram' ||
    path === '/auth/telegram-webapp';
  if (!isAuthBootstrap && portalSessionId) {
    headers[sessionHeaderName] = portalSessionId;
  }
  if (!isAuthBootstrap && csrfToken && method !== 'GET' && method !== 'HEAD') {
    headers[CSRF_HEADER] = csrfToken;
  }

  const response = await fetch(`${API_PREFIX}${path}`, {
    ...options,
    method,
    headers,
    credentials: 'include',
    cache: 'no-store',
  });

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    if (response.status === 413) {
      return {
        success: false,
        error: {
          code: 'file_too_large',
          message:
            'Файл слишком большой (413). В nginx нужен client_max_body_size 55m в server{} (и в location /user/api/ + /vpnbotpanel/api/), затем nginx -t && systemctl reload nginx. Лимит API — 50 MB.',
        },
      };
    }
    const hint =
      response.status === 504
        ? 'Сервер не успел ответить (таймаут nginx). Повторите или проверьте WG/реплики.'
        : response.status === 502
          ? 'Backend не отвечает (502). Проверьте uvicorn.'
          : `API недоступен (${response.status}). Проверьте nginx /user/api/ → backend.`;
    return {
      success: false,
      error: {
        code: 'invalid_response',
        message: hint,
      },
    };
  }

  const payload: unknown = await response.json();

  if (!response.ok) {
    if (payload && typeof payload === 'object' && payload !== null) {
      const body = payload as Record<string, unknown>;
      if (body.error && typeof body.error === 'object') {
        const err = body.error as ApiError;
        return { success: false, error: err };
      }
      const detail = body.detail;
      if (detail && typeof detail === 'object' && detail !== null && 'code' in detail && 'message' in detail) {
        return {
          success: false,
          error: {
            code: String((detail as ApiError).code),
            message: String((detail as ApiError).message),
          },
        };
      }
      if (typeof detail === 'string') {
        return { success: false, error: { code: 'http_error', message: detail } };
      }
    }
    if (response.status === 401) {
      return { success: false, error: { code: 'not_authenticated', message: 'Unauthorized' } };
    }
    return {
      success: false,
      error: { code: 'http_error', message: `Ошибка сервера (${response.status})` },
    };
  }

  if (payload && typeof payload === 'object' && payload !== null && 'success' in payload) {
    return payload as ApiResponse<T>;
  }

  return { success: true, data: payload as T };
}

async function request<T>(path: string, options: RequestInit = {}, allowRetry = true): Promise<ApiResponse<T>> {
  const result = await rawRequest<T>(path, options);
  if (
    allowRetry &&
    !path.startsWith('/auth/') &&
    result.error?.code === 'not_authenticated'
  ) {
    const ok = await ensurePortalSession();
    if (ok) return rawRequest<T>(path, options);
  }
  return result;
}

export const api = {
  getConfig: () => request<PortalPublicConfig>('/auth/config'),
  getMe: () => request<PortalAuthMeResponse>('/auth/me'),
  loginTelegram: (payload: Record<string, unknown>) =>
    request<PortalAuthMeResponse>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  loginTelegramWebApp: (init_data: string) =>
    request<PortalAuthMeResponse>('/auth/telegram-webapp', {
      method: 'POST',
      body: JSON.stringify({ init_data }),
    }),
  loginPassword: (username: string, password: string) =>
    request<PortalAuthMeResponse>('/auth/password', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  setPassword: (password: string) =>
    request<PortalWebsiteAccountInfo>('/auth/password/set', {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  regeneratePassword: () =>
    request<PortalWebsiteAccountInfo>('/auth/password/regenerate', { method: 'POST' }),
  getWebsiteAccount: () =>
    request<PortalWebsiteAccountInfo | null>('/auth/website-account'),
  logout: () => request<{ logged_out: boolean }>('/auth/logout', { method: 'POST' }),
  getProfile: () => request<PortalProfile>('/profile'),
  listVpnKeys: () => request<PortalVpnKeyItem[]>('/vpn/keys'),
  listWgServers: () => request<string[]>('/vpn/servers'),
  listVpnRegions: () => request<PortalRegionItem[]>('/vpn/regions'),
  getVpnConfig: (keyId: number) => request<PortalVpnKeyConfig>(`/vpn/keys/${keyId}/config`),
  createWgDownloadLink: (keyId: number) =>
    request<PortalConfigDownloadLink>(`/vpn/keys/${keyId}/config/download-link`),
  createWgKey: (server_code: string) =>
    request<{ key_id: number; client_id: string; server_code: string }>('/vpn/keys', {
      method: 'POST',
      body: JSON.stringify({ server_code }),
    }),
  deleteWgKey: (keyId: number) =>
    request<{ deleted: boolean; key_id: number }>(`/vpn/keys/${keyId}`, { method: 'DELETE' }),
  listOvpnServers: () => request<string[]>('/ovpn/servers'),
  listOvpnKeys: () => request<PortalOvpnKeyItem[]>('/ovpn/keys'),
  createOvpnKey: (server_code: string) =>
    request<{ key_id: number; profile_id: string; server_code: string }>('/ovpn/keys', {
      method: 'POST',
      body: JSON.stringify({ server_code }),
    }),
  getOvpnConfig: (keyId: number) => request<PortalVpnKeyConfig>(`/ovpn/keys/${keyId}/config`),
  createOvpnDownloadLink: (keyId: number) =>
    request<PortalConfigDownloadLink>(`/ovpn/keys/${keyId}/config/download-link`),
  deleteOvpnKey: (keyId: number) =>
    request<{ deleted: boolean; key_id: number }>(`/ovpn/keys/${keyId}`, { method: 'DELETE' }),
  getMyInvite: () => request<PortalInviteInfo>('/invites/mine'),
  getPromoHistory: () => request<PortalPromoHistory>('/promos/history'),
  getPublicInvite: (code: string) =>
    request<PublicInviteInfo>(`/invites/public/${encodeURIComponent(code)}`),
  updateReferralCode: (code: string) =>
    request<UpdateReferralCodeResponse>('/invites/mine/code', {
      method: 'PATCH',
      body: JSON.stringify({ code }),
    }),
  updateBannerPrefs: (prefs: Record<string, unknown>) =>
    request<{ banner_prefs: Record<string, unknown> }>('/invites/mine/banner-prefs', {
      method: 'PATCH',
      body: JSON.stringify({ prefs }),
    }),
  listPayments: () => request<PortalPaymentItem[]>('/payments'),
  redeemPromo: (code: string) =>
    request<RedeemPromoResponse>('/promos/redeem', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),
  getSubscriptionOffers: () => request<PortalSubscriptionOffers>('/subscription/offers'),
  getTierChangePreview: (tier_id: string, period: string) =>
    request<PortalTierChangePreview | null>(
      `/subscription/tier-change-preview?tier_id=${encodeURIComponent(tier_id)}&period=${encodeURIComponent(period)}`,
    ),
  createYookassaPayment: (tier_id: string, period: string) =>
    request<CreatePortalPaymentResponse>('/subscription/pay/yookassa', {
      method: 'POST',
      body: JSON.stringify({ tier_id, period }),
    }),
  getPaymentStatus: (paymentId: string) =>
    request<PortalPaymentStatusResponse>(`/subscription/payments/${encodeURIComponent(paymentId)}/status`),
  cancelPayment: (paymentId: string) =>
    request<PortalPaymentStatusResponse>(`/subscription/payments/${encodeURIComponent(paymentId)}/cancel`, {
      method: 'POST',
    }),
  listSupportTickets: () =>
    request<PortalSupportTicketListItemDto[]>(`/support/tickets?_=${Date.now()}`),
  getSupportTicket: (ticketId: number) =>
    request<PortalSupportTicketDetailDto>(`/support/tickets/${ticketId}?_=${Date.now()}`),
  createSupportTicket: (subject: string, body: string) =>
    request<PortalSupportTicketDetailDto>('/support/tickets', {
      method: 'POST',
      body: JSON.stringify({ subject, body }),
    }),
  createSupportTicketMedia: (subject: string, body: string, files: Blob | Blob[]) => {
    const fd = new FormData();
    fd.append('subject', subject);
    if (body) fd.append('body', body);
    const list = Array.isArray(files) ? files : [files];
    list.forEach((f, i) => {
      const name = f instanceof File ? f.name : `attachment-${i}`;
      fd.append('files', f, name);
    });
    return request<PortalSupportTicketDetailDto>('/support/tickets/media', {
      method: 'POST',
      body: fd,
    });
  },
  replySupportTicket: (ticketId: number, body: string) =>
    request<PortalSupportTicketDetailDto>(`/support/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),
  replySupportTicketMedia: (
    ticketId: number,
    file: Blob | Blob[],
    opts?: {
      caption?: string;
      durationMs?: number;
      asVoice?: boolean;
      asSticker?: boolean;
      asGif?: boolean;
      asVideoNote?: boolean;
      filename?: string;
    },
  ) => {
    const fd = new FormData();
    const list = Array.isArray(file) ? file : [file];
    list.forEach((f, i) => {
      const name =
        opts?.filename && list.length === 1
          ? opts.filename
          : f instanceof File
            ? f.name
            : opts?.asVoice
              ? 'voice.webm'
              : opts?.asVideoNote
                ? 'video-note.webm'
                : `attachment-${i}`;
      fd.append('files', f, name);
    });
    if (opts?.caption) fd.append('caption', opts.caption);
    if (opts?.durationMs != null) fd.append('duration_ms', String(opts.durationMs));
    if (opts?.asVoice) fd.append('as_voice', 'true');
    if (opts?.asSticker) fd.append('as_sticker', 'true');
    if (opts?.asGif) fd.append('as_gif', 'true');
    if (opts?.asVideoNote) fd.append('as_video_note', 'true');
    return request<PortalSupportTicketDetailDto>(`/support/tickets/${ticketId}/messages/media`, {
      method: 'POST',
      body: fd,
    });
  },
  setSupportReaction: (ticketId: number, messageId: number, emoji: string) =>
    request<PortalSupportTicketDetailDto>(`/support/tickets/${ticketId}/messages/${messageId}/reactions`, {
      method: 'PUT',
      body: JSON.stringify({ emoji }),
    }),
  closeSupportTicket: (ticketId: number) =>
    request<PortalSupportTicketDetailDto>(`/support/tickets/${ticketId}/close`, { method: 'POST' }),
  editSupportMessage: (messageId: number, body: string) =>
    request<PortalSupportTicketDetailDto>(`/support/messages/${messageId}`, {
      method: 'PATCH',
      body: JSON.stringify({ body }),
    }),
  deleteSupportMessage: (messageId: number) =>
    request<PortalSupportTicketDetailDto>(`/support/messages/${messageId}`, { method: 'DELETE' }),
  forwardSupportMessage: (messageId: number) =>
    request<PortalSupportTicketDetailDto>(`/support/messages/${messageId}/forward`, {
      method: 'POST',
    }),
  forwardSupportMessages: (ticketId: number, messageIds: number[], caption = '') =>
    request<PortalSupportTicketDetailDto>(`/support/tickets/${ticketId}/forward`, {
      method: 'POST',
      body: JSON.stringify({ message_ids: messageIds, caption }),
    }),
  supportMediaUrl: (messageId: number, bust?: string | number, index = 0) => {
    const sid = getPortalSessionId();
    const qs = new URLSearchParams();
    if (sid) qs.set('session_id', sid);
    qs.set('v', String(bust ?? messageId));
    if (index > 0) qs.set('index', String(index));
    return `${API_PREFIX}/support/messages/${messageId}/media?${qs.toString()}`;
  },
  getUiSettings: () =>
    request<{ theme: string; nav_position: string }>('/settings/ui'),
  updateUiSettings: (payload: { theme?: string; nav_position?: string }) =>
    request<{ theme: string; nav_position: string }>('/settings/ui', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  getPushVapidKey: () => request<{ public_key: string }>('/support/push/vapid-key'),

  subscribePush: (body: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    request<{ subscribed: boolean }>('/support/push/subscribe', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  unsubscribePush: (endpoint: string) =>
    request<{ unsubscribed: boolean }>('/support/push/subscribe', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    }),
};
